import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Form,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import {
  useCreateMailbox,
  useCustomerHostingServices,
  useCustomerSearch,
  useDeleteMailbox,
  useHostingDomains,
  useHostingMailboxes,
  usePromoteDomain,
} from './useNexusHostingOps';

/**
 * Nexus Hosting-Operations (Spec 9 von 12).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-hosting-ops.md
 *
 * Customer-Picker → Domain-Liste pro Kunde → Promote-Dialog + Mailbox-CRUD.
 * Operator-Bearer-Pfad via /proxy/nexus-bearer/*.
 */
export function NexusHostingOpsPage() {
  const [picker, setPicker] = useState({ accountId: '', label: '' });
  const [promoteFor, setPromoteFor] = useState(null);
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const domains = useHostingDomains(picker.accountId);
  const mailboxes = useHostingMailboxes(picker.accountId);

  const list = domains.data?.domains ?? [];

  return (
    <div>
      <PageHeader
        title="Nexus · Hosting-Operations"
        subtitle={
          picker.accountId
            ? `${picker.label} · account_id: ${picker.accountId}`
            : 'Customer wählen für Domain-Liste + Mailbox-CRUD'
        }
      />

      <CustomerPicker value={picker} onChange={setPicker} />

      {!picker.accountId && (
        <Alert color="info" className="small">
          Wähle oben einen Customer aus. Wenn das Plugin mehrere Websites
          kennt, wird der `account_id` als <code>&lt;orgId&gt;/&lt;websiteId&gt;</code>{' '}
          getragen (Konvention aus dem Mail-Security-Audit).
        </Alert>
      )}

      {picker.accountId && (
        <Row className="g-3">
          <Col lg="7">
            <Card className="mb-3">
              <CardBody>
                <h6 className="text-uppercase small text-muted mb-2">Domains</h6>
                <DataTable
                  loading={domains.isLoading}
                  error={domains.error}
                  empty={!domains.isLoading && list.length === 0}
                  emptyMessage="Keine Domains für diesen Account."
                  onRetry={() => domains.refetch()}
                >
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th style={{ width: 100 }}>Website</th>
                      <th style={{ width: 80 }}>Typ</th>
                      <th style={{ width: 110 }}>DKIM</th>
                      <th style={{ width: 110 }}>DNS</th>
                      <th style={{ width: 200 }}>Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((d) => (
                      <tr key={d.id ?? d.domain}>
                        <td className="small"><code>{d.domain}</code></td>
                        <td className="small">{d.website_id ?? '—'}</td>
                        <td>
                          <Badge color={d.type === 'primary' ? 'primary' : 'secondary'} pill className="orbit-code-badge-sm">
                            {d.type ?? '—'}
                          </Badge>
                        </td>
                        <td className="small">{d.dkim ? `✓ ${d.dkim}` : '—'}</td>
                        <td className="small">{d.dns_status ?? '—'}</td>
                        <td>
                          <ButtonGroup size="sm">
                            <Button
                              color="info"
                              outline
                              tag={Link}
                              to={`/admin/settings/nexus/mail-security?domain=${encodeURIComponent(d.domain)}`}
                            >
                              Mail-Security
                            </Button>
                            <HasPermission perms={['integrations.write']}>
                              {canWrite && isStaging(d.domain) && (
                                <Button
                                  color="warning"
                                  outline
                                  onClick={() => setPromoteFor(d)}
                                >
                                  Promote
                                </Button>
                              )}
                            </HasPermission>
                          </ButtonGroup>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
              </CardBody>
            </Card>
          </Col>

          <Col lg="5">
            <MailboxesPanel
              accountId={picker.accountId}
              data={mailboxes}
              canWrite={canWrite}
            />
          </Col>
        </Row>
      )}

      <PromoteDomainModal
        accountId={picker.accountId}
        target={promoteFor}
        isOpen={!!promoteFor}
        onClose={() => setPromoteFor(null)}
      />
    </div>
  );
}

function CustomerPicker({ value, onChange }) {
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');
  const [pickedCustomer, setPickedCustomer] = useState(null);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(input.trim()), 250);
    return () => clearTimeout(handle);
  }, [input]);

  const search = useCustomerSearch(debounced);
  const list = search.data?.customers ?? [];

  // Step 2: Services pro gewähltem Customer holen. Die Liste enthält
  // möglicherweise nur Summary-Felder ohne `services[]` — deshalb laden
  // wir Detail nach (siehe Launch-Briefing 2026-06-14 — Picker re-uses
  // die Customer-Liste, expandiert aber pro Selection den Detail-Pfad).
  const detail = useCustomerHostingServices(pickedCustomer?.id);
  const services = detail.data?.services ?? [];

  return (
    <Card className="mb-3">
      <CardBody className="py-2">
        <Row className="g-3 align-items-end">
          <Col md="6">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Customer suchen</Label>
              <Input
                type="search"
                bsSize="sm"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setPickedCustomer(null);
                }}
                placeholder="Display-Name oder External-ID (≥2 Zeichen)"
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Auswahl</Label>
              <Input
                type="text"
                bsSize="sm"
                value={value.accountId ? `${value.label} · ${value.accountId}` : ''}
                placeholder="Noch nichts ausgewählt"
                readOnly
              />
            </FormGroup>
          </Col>
        </Row>

        {debounced.length >= 2 && !pickedCustomer && (
          <ListGroup flush className="mt-2" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {search.isLoading && <ListGroupItem className="small text-muted">Suche…</ListGroupItem>}
            {!search.isLoading && list.length === 0 && (
              <ListGroupItem className="small text-muted">Kein Treffer.</ListGroupItem>
            )}
            {list.map((c) => (
              <ListGroupItem
                key={c.id}
                action
                onClick={() => setPickedCustomer(c)}
              >
                <div className="d-flex justify-content-between gap-2 small">
                  <span>{c.display_name ?? c.displayName ?? '—'}</span>
                  <span className="text-muted">
                    {c.external_id ?? c.externalId ?? ''}
                  </span>
                </div>
              </ListGroupItem>
            ))}
          </ListGroup>
        )}

        {pickedCustomer && (
          <div className="mt-2">
            <div className="small text-muted mb-1">
              Hosting-Service wählen für <strong>{pickedCustomer.display_name ?? pickedCustomer.displayName}</strong>{' '}
              <Button
                size="sm"
                color="link"
                className="p-0 ms-1"
                onClick={() => setPickedCustomer(null)}
              >
                ändern
              </Button>
            </div>
            <ListGroup flush style={{ maxHeight: 220, overflowY: 'auto' }}>
              {detail.isLoading && (
                <ListGroupItem className="small text-muted">Lade Services…</ListGroupItem>
              )}
              {!detail.isLoading && services.length === 0 && (
                <ListGroupItem className="small text-muted">
                  Keine Hosting-Services für diesen Customer. Eventuell läuft das
                  Provisioning noch — siehe Jobs-Monitor.
                </ListGroupItem>
              )}
              {services.map((svc) => (
                <ListGroupItem
                  key={`${svc.id}-${svc.account_id}`}
                  action
                  active={value.accountId === svc.account_id}
                  onClick={() =>
                    onChange({
                      accountId: svc.account_id,
                      label: pickedCustomer.display_name ?? pickedCustomer.displayName,
                    })
                  }
                >
                  <div className="d-flex justify-content-between gap-2 small">
                    <span>{svc.plugin_id ?? svc.kind ?? 'hosting'}</span>
                    <span className="text-muted">{svc.account_id}</span>
                  </div>
                </ListGroupItem>
              ))}
            </ListGroup>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function MailboxesPanel({ accountId, data, canWrite }) {
  const [showAdd, setShowAdd] = useState(false);
  const list = data.data?.mailboxes ?? [];
  const del = useDeleteMailbox();

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="text-uppercase small text-muted mb-0">Mailboxes</h6>
          <HasPermission perms={['integrations.write']}>
            {canWrite && (
              <Button size="sm" color="primary" outline onClick={() => setShowAdd(true)}>
                + Mailbox
              </Button>
            )}
          </HasPermission>
        </div>
        <DataTable
          loading={data.isLoading}
          error={data.error}
          empty={!data.isLoading && list.length === 0}
          emptyMessage="Keine Mailboxes."
          onRetry={() => data.refetch()}
        >
          <thead>
            <tr>
              <th>Adresse</th>
              <th style={{ width: 90 }}>Quota</th>
              <th style={{ width: 80 }}>Catch-All</th>
              <th style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.address}>
                <td className="small"><code>{m.address}</code></td>
                <td className="small">{m.quota_mb ? `${m.quota_mb} MB` : '—'}</td>
                <td>{m.is_catch_all ? '✓' : ''}</td>
                <td>
                  <HasPermission perms={['integrations.write']}>
                    {canWrite && (
                      <Button
                        size="sm"
                        color="link"
                        className="text-danger p-0"
                        onClick={() =>
                          del.mutate({ accountId, address: m.address })
                        }
                        disabled={del.isPending}
                        title="Mailbox löschen"
                      >
                        ✕
                      </Button>
                    )}
                  </HasPermission>
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
        <AddMailboxModal
          accountId={accountId}
          isOpen={showAdd}
          onClose={() => setShowAdd(false)}
        />
      </CardBody>
    </Card>
  );
}

function AddMailboxModal({ accountId, isOpen, onClose }) {
  const [form, setForm] = useState({ username: '', password: '', quota: '', isCatchAll: false });
  const create = useCreateMailbox();
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await create.mutateAsync({
        accountId,
        username: form.username,
        password: form.password,
        quota: form.quota ? Number(form.quota) : undefined,
        isCatchAll: form.isCatchAll,
      });
      setForm({ username: '', password: '', quota: '', isCatchAll: false });
      onClose();
    } catch {
      /* Toast vom Client */
    }
  };
  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>Neue Mailbox</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>Adresse (z.B. info@kunde.de)</Label>
            <Input
              type="email"
              value={form.username}
              onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Passwort</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              minLength={10}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>Quota (MB, optional)</Label>
            <Input
              type="number"
              min={50}
              value={form.quota}
              onChange={(e) => setForm((f) => ({ ...f, quota: e.target.value }))}
              placeholder="Plugin-Default wenn leer"
            />
          </FormGroup>
          <FormGroup check>
            <Input
              type="checkbox"
              id="ho-catchall"
              checked={form.isCatchAll}
              onChange={(e) => setForm((f) => ({ ...f, isCatchAll: e.target.checked }))}
            />
            <Label check for="ho-catchall">Catch-All-Mailbox</Label>
          </FormGroup>
          <Alert color="info" className="small mt-3 mb-0">
            Plugins die <code>createMailbox</code> nicht unterstützen, antworten
            <code> 400 mailbox_not_supported</code> — der Submit wirft dann einen
            Toast und nichts wird verändert.
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
          <Button color="primary" type="submit" disabled={create.isPending}>
            {create.isPending ? 'Lege an…' : 'Anlegen'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function PromoteDomainModal({ accountId, target, isOpen, onClose }) {
  return (
    <PromoteDomainModalBody
      key={target?.id ?? 'idle'}
      accountId={accountId}
      target={target}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
}

function PromoteDomainModalBody({ accountId, target, isOpen, onClose }) {
  const [domain, setDomain] = useState('');
  const promote = usePromoteDomain();
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await promote.mutateAsync({ accountId, domain });
      onClose();
    } catch {
      /* Toast vom Client */
    }
  };
  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <ModalHeader toggle={onClose}>Staging-Domain promoten</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <p className="small text-muted">
            Promote die Staging-Domain <code>{target?.domain ?? '—'}</code> auf
            die echte Customer-Domain. Backend wirft
            <code> 409 domain_already_attached</code> wenn die Ziel-Domain schon
            angehängt ist.
          </p>
          <FormGroup>
            <Label>Neue Final-Domain</Label>
            <Input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="kundendomain.de"
              required
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose}>Abbrechen</Button>
          <Button color="warning" type="submit" disabled={promote.isPending || !domain.trim()}>
            {promote.isPending ? 'Promote läuft…' : 'Promoten'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

function isStaging(domain) {
  if (!domain) return false;
  return /\.staging\.valitype\.net$/i.test(domain) || /\.staging\./i.test(domain);
}
